import { useEffect, useState } from "react";
import type {
  ApiBudget,
  ApiPerson,
  ApiProject,
  ApiPyramidLevel,
  ApiTask,
  ApiWorkspace,
} from "@pms/shared";

export const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  "https://pms-api.ekobrott.workers.dev";

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { message?: string };
      detail = body?.message ?? "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export type Async<T> = { data: T | null; loading: boolean; error: string | null };

/** Small fetch-on-mount hook. Swap for React Query if the app grows. */
export function useApi<T>(path: string): Async<T> {
  const [state, setState] = useState<Async<T>>({ data: null, loading: true, error: null });

  useEffect(() => {
    let alive = true;
    setState({ data: null, loading: true, error: null });
    get<T>(path)
      .then((data) => alive && setState({ data, loading: false, error: null }))
      .catch((e: Error) => alive && setState({ data: null, loading: false, error: e.message }));
    return () => {
      alive = false;
    };
  }, [path]);

  return state;
}

export const useWorkspace = () => useApi<ApiWorkspace>("/v1/workspace");
export const usePeople = () => useApi<{ people: ApiPerson[] }>("/v1/people");
export const usePyramid = () => useApi<{ levels: ApiPyramidLevel[] }>("/v1/pyramid");
export const useProjects = () => useApi<{ projects: ApiProject[] }>("/v1/projects");
export const useTasks = () => useApi<{ tasks: ApiTask[] }>("/v1/tasks");
export const useBudget = () => useApi<ApiBudget>("/v1/budget");

/** POST a report and hand the browser a .txt download. */
export async function downloadReport(reportId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/v1/reports/${reportId}`, { method: "POST" });
  if (!res.ok) throw new Error(`Report failed: ${res.status}`);

  const disp = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disp);
  const filename = match?.[1] ?? `${reportId}.txt`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function fmtMoney(n: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}
